import {
  Tariff,
  Status,
  Provider,
  ClaimChange,
  ArrayFieldChange,
  PrimitiveFieldChange,
  RelationFieldChange,
} from 'interfaces';
import { startCase } from 'lodash';
import { Text } from '@mantine/core';
import { DiffEdit } from 'deep-diff';
import type { UnifiedEvent } from './ClaimBillingTimeline';

export const formatField = (field: string): string => {
  if (field.includes('icd9') || field === 'icd9') return 'ICD9';
  let formatted = startCase(field);
  if (formatted.endsWith(' Id') || formatted.endsWith(' ID')) {
    formatted = formatted.slice(0, -3);
  }
  if (field === 'referringProviderObject') {
    formatted = 'Referring Provider';
  }
  return formatted;
};
export const formatChangeValue = (
  value: any,
  field: string,
  statuses: Status[],
  providers: Provider[],
  objectSummary: {
    detailsUpdated: string;
    itemCount: (count: number) => string;
  } = {
    detailsUpdated: 'Details updated',
    itemCount: (count) => `${count} item${count === 1 ? '' : 's'}`,
  }
) => {
  if (!value) return 'None';
  const provider = providers.find((provider) => provider.id === value);
  const status = statuses.find((statusItem) => statusItem.id === value);
  switch (field) {
    case 'statusID':
      return status?.type || 'Unknown Status';
    case 'providerID':
      return provider
        ? `${provider.firstName} ${provider.lastName}`
        : 'Unknown Provider';
    default:
      if (typeof value === 'object') {
        if (Array.isArray(value)) {
          return objectSummary.itemCount(value.length);
        }
        if (value.firstName && value.lastName) {
          return `${value.firstName} ${value.lastName}`;
        } else if (value.number) {
          return value.number;
        } else if (value.name) {
          return value.name;
        } else {
          return objectSummary.detailsUpdated;
        }
      } else {
        return value.toString();
      }
  }
};

export const formatTariffChangeMessage = (
  action: string,
  tariff: Tariff,
  green: string,
  red: string
) => {
  return (
    <>
      {startCase(action)} {tariff.units && <>{tariff.units} Unit(s) of </>}
      Tariff{' '}
      <Text component="span" fw="bold" c={action === 'added' ? green : red}>
        {`${tariff.prefix}${tariff.code}`}
      </Text>
    </>
  );
};

// This function takes an array of differences
// and returns an object with the key being the path of the
// difference and the value being an object with
// the before and after values
export const transformDifferences = (
  differences: DiffEdit<any, any>[]
): { [key: string]: { before: any; after: any } } =>
  differences.reduce((acc, diff) => {
    if (diff.kind === 'E' && diff.path && diff.path.length > 0) {
      const key = diff.path.join('.');
      acc[key] = { before: diff.lhs, after: diff.rhs };
    }
    return acc;
  }, {} as { [key: string]: { before: any; after: any } });

export const normalizeChanges = (data: ClaimChange[]) => {
  let changesArray: ClaimChange[] = [];
  if (Array.isArray(data)) {
    return data;
    //TODO: get rid of this once we run the script on prod
    // data should always be an array of objects
  } else if (data && typeof data === 'object') {
    changesArray = Object.entries(data).map(([field, keys]) => {
      const changeData = keys as
        | PrimitiveFieldChange
        | RelationFieldChange
        | ArrayFieldChange;

      let type: 'primitive' | 'relation' | 'array';
      if (
        typeof changeData.after === 'string' ||
        typeof changeData.after === 'number' ||
        changeData === null
      ) {
        type = 'primitive';
      } else if (Array.isArray(changeData.after)) {
        type = 'array';
      } else if (typeof changeData.after === 'object') {
        type = 'relation';
      } else {
        type = 'primitive';
      }
      return {
        type,
        field,
        ...(typeof changeData === 'object' && changeData !== null
          ? changeData
          : {}),
      } as PrimitiveFieldChange | RelationFieldChange | ArrayFieldChange;
    });
  }
  return changesArray;
};

const normalizeHistoryValue = (value: unknown) =>
  value === null || value === undefined ? '' : String(value).trim();

const isDuplicateHospitalNameChange = (
  change: ClaimChange,
  hospitalCodeChange: ClaimChange | undefined
) =>
  change.field === 'hospitalName' &&
  change.type === 'primitive' &&
  hospitalCodeChange?.field === 'hospitalCode' &&
  hospitalCodeChange.type === 'primitive' &&
  normalizeHistoryValue(change.before) ===
    normalizeHistoryValue(hospitalCodeChange.before) &&
  normalizeHistoryValue(change.after) ===
    normalizeHistoryValue(hospitalCodeChange.after);

const NON_DISPLAY_RELATION_DIFF_FIELDS = new Set([
  'id',
  'createdAt',
  'updatedAt',
  'version',
]);

const DISPLAYABLE_ARRAY_FIELDS = new Set(['tariffs', 'debits']);

const DISPLAYABLE_TARIFF_DIFF_FIELDS = new Set([
  'anesthesiaUnits',
  'code',
  'icd9',
  'incision',
  'isManualTotal',
  'percentage',
  'prefix',
  'rate',
  'serviceEnd',
  'serviceStart',
  'split',
  'status',
  'total',
  'units',
]);

const DISPLAYABLE_DEBIT_DIFF_FIELDS = new Set([
  'adjustment',
  'microfilm',
  'paidAmount',
  'requestedAmount',
  'source',
  'status',
  'type',
]);

const getDiffField = (diff: { path?: unknown[] }) =>
  Array.isArray(diff.path) && diff.path.length === 1
    ? String(diff.path[0])
    : null;

const filterDisplayableDifferences = <T extends { path?: unknown[] }>(
  differences: T[],
  allowedFields: Set<string>
) =>
  differences.filter((difference) => {
    const field = getDiffField(difference);
    return field !== null && allowedFields.has(field);
  });

export const filterDisplayableTariffDifferences = <
  T extends { path?: unknown[] }
>(
  differences: T[]
) => filterDisplayableDifferences(differences, DISPLAYABLE_TARIFF_DIFF_FIELDS);

export const filterDisplayableDebitDifferences = <
  T extends { path?: unknown[] }
>(
  differences: T[]
) => filterDisplayableDifferences(differences, DISPLAYABLE_DEBIT_DIFF_FIELDS);

const getDisplayableArrayChange = (
  change: ArrayFieldChange
): ArrayFieldChange | null => {
  if (!DISPLAYABLE_ARRAY_FIELDS.has(change.field)) return null;

  const allowedFields =
    change.field === 'tariffs'
      ? DISPLAYABLE_TARIFF_DIFF_FIELDS
      : DISPLAYABLE_DEBIT_DIFF_FIELDS;
  const modified = (change.modified ?? [])
    .map((item) => ({
      ...item,
      differences: filterDisplayableDifferences(
        item.differences ?? [],
        allowedFields
      ),
    }))
    .filter((item) => item.differences.length > 0);
  const hasStructuredChanges = Boolean(
    change.added?.length || change.removed?.length || change.modified?.length
  );
  const hasLegacyAfterOnlyDebits =
    change.field === 'debits' &&
    !hasStructuredChanges &&
    Boolean(change.after?.length);

  if (
    !(
      change.added?.length ||
      change.removed?.length ||
      modified.length ||
      hasLegacyAfterOnlyDebits
    )
  ) {
    return null;
  }

  return { ...change, modified };
};

const getDisplayableRelationChange = (
  change: RelationFieldChange
): RelationFieldChange | null => {
  const { before, after, differences = [] } = change;

  if (!before && !after) return null;
  if (!before || !after || before.id !== after.id) return change;

  const displayableDifferences = differences.filter((difference) => {
    if (difference.kind !== 'E' || difference.path?.length !== 1) return false;
    return !NON_DISPLAY_RELATION_DIFF_FIELDS.has(String(difference.path[0]));
  });
  return displayableDifferences.length > 0
    ? { ...change, differences: displayableDifferences }
    : null;
};

export const collapseDuplicateHospitalChanges = (
  changes: ClaimChange[]
): ClaimChange[] => {
  const hospitalCodeChange = changes.find(
    (change) => change.field === 'hospitalCode' && change.type === 'primitive'
  );
  const displayableChanges: ClaimChange[] = [];

  changes.forEach((change) => {
    if (
      hospitalCodeChange &&
      isDuplicateHospitalNameChange(change, hospitalCodeChange)
    ) {
      return;
    }
    if (change.type !== 'relation') {
      displayableChanges.push(change);
      return;
    }
    const displayableChange = getDisplayableRelationChange(change);
    if (displayableChange) displayableChanges.push(displayableChange);
  });

  return displayableChanges;
};

export const getDisplayableClaimChanges = (
  changes: ClaimChange[]
): ClaimChange[] => {
  const displayableChanges: ClaimChange[] = [];

  collapseDuplicateHospitalChanges(normalizeChanges(changes)).forEach(
    (change) => {
      if (change.type !== 'array') {
        displayableChanges.push(change);
        return;
      }
      const displayableChange = getDisplayableArrayChange(change);
      if (displayableChange) displayableChanges.push(displayableChange);
    }
  );

  return displayableChanges;
};

const getTariffCode = (tariff: Partial<Tariff> | undefined): string => {
  if (!tariff) return 'Unknown';
  const prefix = tariff.prefix ?? '';
  const code = tariff.code ?? '';
  const label = `${prefix}${code}`;
  return label.trim() || 'Unknown';
};

export const getActivityEventsFromHistory = (history: {
  id: string;
  updatedAt?: string | Date | null;
  changes: ClaimChange[];
}): UnifiedEvent[] => {
  const changes = normalizeChanges(history.changes);
  const events: UnifiedEvent[] = [];

  changes.forEach((change, changeIndex) => {
    const eventDate =
      change.updatedAt ||
      (history.updatedAt instanceof Date
        ? history.updatedAt.toISOString()
        : history.updatedAt);

    if (change.field === 'tariffs' && change.type === 'array') {
      const beforeTariffs = new Map(
        ((change.before ?? []) as Partial<Tariff>[]).map((tariff) => [
          tariff.id,
          tariff,
        ])
      );
      const afterTariffs = new Map(
        ((change.after ?? []) as Partial<Tariff>[]).map((tariff) => [
          tariff.id,
          tariff,
        ])
      );

      change.modified?.forEach((modified, modifiedIndex) => {
        modified.differences
          ?.filter((diff) => diff.path?.join('.') === 'status')
          .forEach((diff, diffIndex) => {
            const afterTariff = afterTariffs.get(modified.id);
            const beforeTariff = beforeTariffs.get(modified.id);
            events.push({
              id: `tariff-status-${history.id}-${modified.id}-${modifiedIndex}-${diffIndex}`,
              kind: 'tariffStatus',
              date: eventDate,
              tariffCode: getTariffCode(afterTariff ?? beforeTariff),
              beforeStatus: diff.lhs ?? beforeTariff?.status ?? null,
              afterStatus: diff.rhs ?? afterTariff?.status ?? null,
            });
          });
      });
    }

    if (
      change.type === 'activity' &&
      (change.activity === 'internalCommentAdded' ||
        change.activity === 'internalCommentRemoved')
    ) {
      events.push({
        id: `comment-${history.id}-${changeIndex}`,
        kind: 'comment',
        date: eventDate,
        comment: change.commentText ?? null,
        commentAction:
          change.activity === 'internalCommentRemoved' ? 'removed' : 'added',
      });
    }

    if (change.field === 'internalComments') {
      const comment =
        typeof change.after === 'string'
          ? change.after
          : change.after
          ? String(change.after)
          : null;
      events.push({
        id: `comment-${history.id}-${changeIndex}`,
        kind: 'comment',
        date: eventDate,
        comment,
      });
    }
  });

  return events;
};
